<template>
  <div class="create-tag-dialog">
    <header class="dialog-header">
      <div class="dialog-eyebrow">New</div>
      <h2 class="dialog-title">Tag</h2>
      <p class="dialog-subtitle">Create a tag to group findings (e.g. a CAPEX project) across your organization.</p>
    </header>

    <v-form ref="form" @submit.prevent="handleSubmit">
      <div class="form-grid">
        <v-text-field v-model="name" label="Name" variant="outlined" density="comfortable" hide-details="auto"
          class="input-field" required />

        <v-select v-model="group" :items="groups" label="Group (optional)" variant="outlined"
          density="comfortable" hide-details="auto" class="input-field" clearable
          placeholder="e.g. Project, Discipline" />

        <v-textarea v-model="description" label="Description (optional)" variant="outlined" density="comfortable"
          hide-details="auto" class="input-field" rows="2" auto-grow
          placeholder="What this tag is for" />

        <div class="color-field">
          <span class="color-field-label">Colour</span>
          <div class="swatches">
            <button v-for="c in colorSwatches" :key="c" type="button" class="swatch"
              :class="{ 'is-selected': sameColor(c, color) }" :style="{ background: c }" :title="c" @click="color = c" />
            <label class="swatch custom-swatch" :style="{ background: color }" title="Custom colour">
              <v-icon size="14" color="#fff">mdi-plus</v-icon>
              <input type="color" v-model="color" class="color-input" />
            </label>
          </div>
        </div>

        <TagGlyphPicker v-model="icon" :color="color" />

        <div class="preview-row">
          <span class="preview-label">Preview</span>
          <TagChip :name="name" :color="color" :icon="icon" placeholder="Tag name" />
        </div>
      </div>

      <div class="actions">
        <button type="button" class="ghost-btn" :disabled="isSubmitting" @click="handleCancel">Cancel</button>
        <button type="submit" class="primary-btn" :disabled="!isFormComplete || isSubmitting">
          <v-progress-circular v-if="isSubmitting" indeterminate size="16" width="2" color="#071514" />
          <span v-else>Create Tag</span>
        </button>
      </div>
    </v-form>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits } from 'vue';
import CreateTagModel from '@/domain/tag/CreateTagModel';
import TagService from '@/services/TagService';
import { useOrganizationStore } from '@/store/organizationStore';
import { MARKER_COLOR_SWATCHES, DEFAULT_MARKER_COLOR } from '@/lib/markerShapeFactory';
import { TAG_GROUPS } from '@/constants/tagOptions';
import TagChip from '@/components/tags/TagChip.vue';
import TagGlyphPicker from '@/components/tags/TagGlyphPicker.vue';

const emit = defineEmits(['onTagCreated', 'onCloseWindow']);

const name = ref('');
const group = ref(null);
const description = ref('');
const color = ref(DEFAULT_MARKER_COLOR);
const icon = ref(null);
const isSubmitting = ref(false);
const organizationStore = useOrganizationStore();
const form = ref(null);

const groups = TAG_GROUPS;
const colorSwatches = MARKER_COLOR_SWATCHES;
const sameColor = (a, b) => (a || '').toLowerCase() === (b || '').toLowerCase();

const isFormComplete = computed(() => !!name.value.trim());

function resetForm() {
  name.value = '';
  group.value = null;
  description.value = '';
  color.value = DEFAULT_MARKER_COLOR;
  icon.value = null;
  form.value?.resetValidation?.();
}

function handleCancel() {
  resetForm();
  emit('onCloseWindow');
}

async function handleSubmit() {
  if (!isFormComplete.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    const descriptionValue = description.value.trim() || null;
    const model = new CreateTagModel(
      organizationStore.organizationId, name.value.trim(), color.value,
      group.value || null, descriptionValue, icon.value || null,
    );
    await TagService.createTag(model);
  } finally {
    emit('onTagCreated');
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
.create-tag-dialog {
  padding: 4px;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: min(420px, 80vw);
}

.dialog-header { display: flex; flex-direction: column; gap: 4px; }
.dialog-eyebrow { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8aa0aa; }
.dialog-title { font-size: 1.4rem; font-weight: 700; margin: 0; color: #fff; }
.dialog-subtitle { font-size: 0.9rem; color: #b0bec5; margin: 0; line-height: 1.5; }

.form-grid { display: flex; flex-direction: column; gap: 12px; }
.input-field :deep(.v-field) { border-radius: 10px; background: rgba(255, 255, 255, 0.03); }

.color-field { display: flex; flex-direction: column; gap: 8px; }
.color-field-label { font-size: 0.78rem; color: #b0bec5; font-weight: 600; }
.swatches { display: flex; flex-wrap: wrap; gap: 8px; }
.swatch {
  width: 30px; height: 30px; border-radius: 8px; border: 2px solid rgba(255, 255, 255, 0.12);
  cursor: pointer; padding: 0; display: inline-flex; align-items: center; justify-content: center;
  transition: transform 0.1s ease, border-color 0.1s ease;
}
.swatch:hover { transform: scale(1.08); }
.swatch.is-selected { border-color: #fff; box-shadow: 0 0 0 2px rgba(100, 255, 218, 0.5); }
.custom-swatch { position: relative; overflow: hidden; }
.color-input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }

.preview-row { display: flex; align-items: center; gap: 10px; margin-top: 2px; }
.preview-label { font-size: 0.78rem; color: #b0bec5; font-weight: 600; }

.actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 4px; }
.ghost-btn {
  padding: 10px 18px; border-radius: 10px; background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06); color: #cfd8dc; font-weight: 600; font: inherit; cursor: pointer;
  transition: background 0.15s ease;
}
.ghost-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.08); }
.primary-btn {
  padding: 10px 22px; border-radius: 10px; background: var(--duallo-accent-gradient); color: #071514;
  font-weight: 700; border: none; font: inherit; cursor: pointer; min-width: 140px;
  display: flex; align-items: center; justify-content: center;
}
.primary-btn:disabled, .ghost-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
